import instance, { ApiResponse } from 'api';
import Button from 'components/button/Button';
import { Modal } from 'components/modal/Modal';
import { Spinner } from 'components/spinner/Spinner';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, selectModalState, ModalTypes } from 'reducers/modal';
import WarningIcon from 'assets/Icon_Warning.png';
import { Toast } from 'helpers/toast';
import { SelectOption } from 'components/form/Dropdown';
import { endpoints } from 'endpoints.config';
import Select, { components } from 'react-select';

type DepositAddressInfo = {
    address: string;
    qrCode: string;
};

export const DepositCryptoModal = ({ asset }: { asset: string | null }) => {
    const { t } = useTranslation('crypto');
    const dispatch = useDispatch();
    const [addressData, setAddressData] = useState<DepositAddressInfo | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const modalState = useSelector(selectModalState);
    const [availableCryptos, setAvailableCryptos] = useState<null | SelectOption[]>(null);
    const [selectedCrypto, setSelectedCrypto] = useState<string | null>(null);

    useEffect(() => {
        if (
            modalState.modalType !== ModalTypes.DEPOSIT_CRYPTO ||
            (!modalState.data.crypto && !selectedCrypto)
        )
            return; // the modaltype check lets typescript infer the modalstate's data
        setAddressData(null);
        instance
            .get<ApiResponse<DepositAddressInfo>>(modalState.data.depositEndpoint, {
                params: { asset: modalState.data.crypto ?? selectedCrypto },
            })
            .then((res) => setAddressData(res.data.details))
            .catch((err) =>
                setErrorMessage(
                    t('cantGenerateAddress')
                )
            );
    }, [modalState, selectedCrypto]);
    useEffect(() => {
        if (modalState.modalType !== ModalTypes.DEPOSIT_CRYPTO || modalState.data.crypto) return;
        instance
            .get<ApiResponse<SelectOption[]>>(endpoints.cryptoExchange.getDepositOptions)
            .then((res) => setAvailableCryptos(res.data.details))
            .catch(() => {
                Toast.openErrorToast(t('errorLoadingCrypto'));
                dispatch(closeModal());
            });
    }, [modalState, dispatch]);
    const handleCopy = () => {
        const addressToCopy = addressData?.address;
        if (addressToCopy) {
            Toast.openSuccessToast(t('copied', { ns: 'common' }));
            window.navigator.clipboard.writeText(addressToCopy);
        }
    };
    if (modalState.modalType !== ModalTypes.DEPOSIT_CRYPTO) return null;
    const depositElement =
        addressData === null ? (
            <div className="DepositCryptoModal Loading">
                <p>{t('generateAddress')}</p>
                <div style={{ position: 'relative', width: '100%', height: 10 }}>
                    <Spinner />
                </div>
            </div>
        ) : (
            <div className="DepositCryptoModal">
                <p className="notranslate" translate="no">{t('useQrCode')}</p>
                {(asset === 'USDC' || selectedCrypto === 'USDC') && (
                    <div className="UsdcWarning notranslate" translate="no">
                        <img src={WarningIcon} alt="Attention" />
                        {t('doNotDepositWarning')}
                    </div>
                )}
                <img src={`data:image/jpeg;base64, ${addressData.qrCode}`} alt="Deposit QR code" />
                <p>{addressData.address}</p>
                <Button className="CopyButton" priority="secondary" onClick={handleCopy}>
                    {t('copy', { ns: 'common' })}
                </Button>
            </div>
        );
        const SingleValue = (props: any) => (
            <components.SingleValue {...props}>
                <span className="notranslate" translate="no">
                    {props.children}
                </span>
            </components.SingleValue>
        );
        const Option = (props: any) => (
            <components.Option {...props}>
                <span className="notranslate" translate="no">
                    {props.data.label}
                </span>
            </components.Option>
        );

    return (
        <Modal
            title={
                <>
                    Deposit{' '}
                    <span className="notranslate" translate="no">
                        {modalState.data.crypto ?? selectedCrypto ?? 'Crypto'}
                    </span>
                </>
            }
            primaryButton={t('close', { ns: 'common' })}
            handlePrimary={() => dispatch(closeModal())}
        >
            {errorMessage ? (
                <p>{errorMessage}</p>
            ) : modalState.data.crypto === null ? (
                availableCryptos ? (
                    <div className="DepositCryptoModalWrapper WithSelector">
                        <Select
                            options={availableCryptos}
                            onChange={(item) => setSelectedCrypto(item?.value ?? null)}
                            className="CustomSelect"
                            classNamePrefix="CustomSelect"
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: (provided: any) => ({
                                    ...provided,
                                    zIndex: 500,
                                }),
                            }}
                            components={{
                                SingleValue,
                                Option,
                                IndicatorSeparator: null,
                            }}
                        />
                        {!!selectedCrypto && depositElement}
                    </div>
                ) : (
                    <div className="DepositCryptoModal Loading">
                        <p>{t('loadingCrypto')}</p>
                        <div style={{ position: 'relative', width: '100%', height: 10 }}>
                            <Spinner />
                        </div>
                    </div>
                )
            ) : (
                depositElement
            )}
        </Modal>
    );
};
